/*!

=========================================================
* DIGIMONDO Industry Solution UI - v0.0.1
=========================================================

* Product Page: https://digimondo.de
* Copyright 2019 DIGIMONDO GmbH (https://www.digimondo.de)
* Licensed under MIT (https://github.com/creativetimofficial/vuetify-material-dashboard/blob/master/LICENSE.md)

=========================================================

*/

// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
import Vue from 'vue';
import { sentrySetup } from './sentry';

// import VueLog from '@dreipol/vue-log'

// Plugins
import './plugins';

// Sync router with store
import { sync } from 'vuex-router-sync';

// Application imports
import vuetify from './plugins/vuetify';
import App from './App';
import i18n from '@/i18n';
import router from '@/router';
import store from '@/store';
import { EventBus } from './services/eventbus.js';

// register service worker
// TODO: remove after a few months when we are sure there is not a single client left with an old service worker
window.addEventListener('load', () => {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register('/service-worker.js');
  }
});

// custom polyfill implementation for Promise.finally on Edge 41
// https://stackoverflow.com/a/53327815
// eslint-disable-next-line no-extend-native
Promise.prototype.finally =
  Promise.prototype.finally ||
  {
    finally(fn) {
      const onFinally = (value) => Promise.resolve(fn()).then(() => value);
      return this.then(
        (result) => onFinally(result),
        (reason) => onFinally(Promise.reject(reason))
      );
    },
  }.finally;

// Vue.use(VueLog)

// Sync store with router
sync(store, router);

Vue.config.productionTip = false;

// Warning and Error Handler
Vue.config.warnHandler = function (msg, vm, trace) {
  console.warn(`Warn: ${msg}\nTrace: ${trace}`);
};
Vue.config.errorHandler = function (err, vm, info) {
  console.error(`Error: ${err.toString()}\nInfo: ${info}`);
  console.error(err);
};

// Sentry will overwrite Vue.config.errorHandler but stores a reference
// and will invoke it after the error has been handled by Sentry
sentrySetup();

const checkPermissions = (permissionSet, userPermissions) => {
  // {
  //   or: [
  //     {and: [
  //       { subject: 'DigitalTwin', action: 'manage' },
  //       { subject: 'AutoID', action: 'read', inverted: true }
  //     ]},
  //     { subject: 'all', action: 'manage' }
  //   ]
  // }

  if (permissionSet.or && Array.isArray(permissionSet.or)) {
    return permissionSet.or.find((permission) => checkPermissions(permission, userPermissions));
  } else if (permissionSet.and && Array.isArray(permissionSet.and)) {
    return permissionSet.and.every((permission) => checkPermissions(permission, userPermissions));
  }

  const hasPermission = userPermissions[`${permissionSet.subject}.${permissionSet.action}`];
  return hasPermission === !permissionSet.inverted;
};

const verifyPermissions = (to, next, defaultHome, userPermissions) => {
  if (to.meta && to.meta.permissions) {
    if (!checkPermissions(to.meta.permissions, userPermissions)) {
      console.warn('HAS NO PERMISSIONS', defaultHome, to.meta.permissions);
      if (to.path === defaultHome) {
        // avoid endless loops, if the user does not have permissions for the default-home,
        // forward to the "default" home
        if (to.path === '/home') {
          next('/error');
        } else {
          next('/home');
        }
      } else {
        next(defaultHome);
      }
      return;
    }
  }
  next();
};

router.beforeEach((to, from, next) => {
  let needsAuth = true;
  if (to.meta) {
    needsAuth = !to.meta.noAuth;
  }
  const homeLink = store.state.app.sidebarIconLink || '/home';

  if (!needsAuth) {
    next();
  } else {
    store
      .dispatch('authentication/verify')
      .then(async () => {
        if (!store.state.app.permissions) {
          await store.dispatch('app/loadPermissions');
        }
        verifyPermissions(to, next, homeLink, store.state.app.permissions);
      })
      .catch((error) => {
        console.error('Error during navigation', error);
        next(`/login?redirect=${to.fullPath}`);
      });
  }
});

// Add a global mixin
const AsyncVueI18nPlugin = {
  install: function (Vue) {
    Vue.prototype.$i18n_t = function () {
      return this.$root.$children[0].$t(...arguments);
    };

    Vue.prototype.$i18n_tc = function () {
      return this.$root.$children[0].$tc(...arguments);
    };

    Vue.prototype.$i18n_te = function () {
      return this.$root.$children[0].$te(...arguments);
    };

    Vue.prototype.$i18n_d = function () {
      return this.$root.$children[0].$d(...arguments);
    };

    Vue.prototype.$i18n_n = function () {
      return this.$root.$children[0].$n(...arguments);
    };
  },
};

Vue.use(AsyncVueI18nPlugin);

// Add a global mixin
const GlobalSnackPlugin = {
  install: function (Vue) {
    Vue.prototype.$snack = function (message, color = 'success', link) {
      if (this._isBeingDestroyed || this._isDestroyed) {
        return;
      }

      const snackbar = { model: true, color, message, link };
      this.$store.state.app.snackbar = snackbar;
    };
  },
};

Vue.use(GlobalSnackPlugin);

/* eslint-disable no-new, vue/require-name-property */
const vueInstance = new Vue({
  vuetify,
  i18n,
  router,
  store,
  render: (h) => h(App),
}).$mount('#app');

EventBus.$on('update-breadcrumbs', () => {
  vueInstance.$children[0].updateBreadcrumbs();
});

EventBus.$on('refresh-branding', () => {
  vueInstance.$children[0].loadBranding();
});

function removePreAppLoadingAnimation() {
  const animationContainer = document.getElementById('pre-app-loading-animation');
  if (animationContainer) {
    animationContainer.remove();
  }
}
removePreAppLoadingAnimation();
